import { createDateFormatter, useDisplayTimezone } from "@nexus/ui";
import {
  CHART_LEGEND_ICONS,
  escapeHtml,
  missingDataMode,
  motionClass,
  positionChartTooltip,
  useAnimatedNumber,
  useChartAnimation,
  useChartLifecycle,
  useWidgetMotion,
  type MissingDataMode,
} from "@nexus/widget-kit";
export { escapeHtml } from "@nexus/widget-kit";
import { useContext, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart, type BarSeriesOption, type LineSeriesOption } from "echarts/charts";
import { LegacyGridContainLabel } from "echarts/features";
import {
  GridComponent,
  TitleComponent,
  AxisPointerComponent,
  VisualMapComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  type GridComponentOption,
  type TitleComponentOption,
  type LegendComponentOption,
  type TooltipComponentOption,
  type MarkAreaComponentOption,
  type AxisPointerComponentOption,
  type VisualMapComponentOption,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  defineLabels,
  firstError,
  resolveChartColors,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  WidgetState,
  MetricStat,
  withAlpha,
  WidgetSkeleton,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { IP_BLOCKING_SERIES_CONTRACT } from "./types";
import type { IpBlockingSeriesData } from "./types";
import { buildCohortOption } from "./cohortOption";
import type { CohortSeriesOptions } from "./cohortOption";

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  TitleComponent,
  AxisPointerComponent,
  VisualMapComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  CanvasRenderer,
  LegacyGridContainLabel,
]);

export type IpBlockingOption = echarts.ComposeOption<
  | BarSeriesOption
  | LineSeriesOption
  | GridComponentOption
  | TitleComponentOption
  | LegendComponentOption
  | TooltipComponentOption
  | MarkAreaComponentOption
  | AxisPointerComponentOption
  | VisualMapComponentOption
>;

export interface IpBlockingTrendConfig {
  title?: string;
  animateValue?: boolean;
  /** `true` draws the chart in; off by default (drawn at once). */
  animateChart?: boolean;
  /** Per-instance overrides of the texts listed in `manifest.labels`. */
  labels?: Record<string, string>;
  /** Also draws the IPs whose blocking an ISP confirmed. */
  showConfirmed?: boolean;
  /** Adds the summary strip and the data note around the chart. */
  showDetails?: boolean;
  /** The latest day's headline above the chart; on by default. */
  showLatest?: boolean;
  /** Show gap (default) keeps the whole week; Fit to data crops the days without data at its ends. */
  missingData?: MissingDataMode;
  /** Detected (or distinct, for blocking windows) bars; on by default. */
  showDetected?: boolean;
  /** Safe-to-block line; on by default. */
  showSafe?: boolean;
  /** Regulator-approved line; off by default. */
  showApproved?: boolean;
  /** Value labels per series; only the safe-to-block line shows them by default. */
  showDetectedLabels?: boolean;
  showSafeLabels?: boolean;
  showApprovedLabels?: boolean;
}

export const LABELS = defineLabels([
  { key: "detected", name: "Detected series", default: "IPs detected" },
  { key: "sent", name: "Sent series", default: "IPs sent for blocking" },
  {
    key: "confirmed",
    name: "Confirmed series",
    default: "Blocking confirmed",
    hint: "Only shown when the widget's confirmed option is on.",
  },
  { key: "yAxis", name: "Value axis title", default: "Unique IPs" },
  {
    key: "notSettled",
    name: "Unconfirmed day (tooltip)",
    default: "Unavailable",
    advanced: true,
    hint: "Shown in the tooltip for days whose confirmations are still arriving.",
  },
  { key: "summaryLatest", name: "Summary: latest day", default: "Latest day", advanced: true },
  { key: "summaryRatio", name: "Summary: sent ratio", default: "Sent / detected", advanced: true },
  { key: "summaryPeak", name: "Summary: peak", default: "Peak detected", advanced: true },
  {
    key: "summaryConfirmed",
    name: "Summary: latest confirmed",
    default: "Latest confirmed",
    advanced: true,
  },
  { key: "emptyTitle", name: "Empty title", default: "No IP blocking data" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "No IPs were reported for this period.",
  },
] as const);

export type IpBlockingLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;
const DEFAULT_LABELS: IpBlockingLabels = resolveLabels(LABELS, undefined);

export const manifest: WidgetManifest = {
  id: "blocking-trend",
  name: "Blocking Trend",
  description:
    "Latest seven days of detected targets and safe-to-block targets for either IP or domain blocking.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    series: {
      source: "ip-blocking-cohorts",
      contract: IP_BLOCKING_SERIES_CONTRACT,
      configurable: true,
      sources: ["ip-blocking-cohorts", "domain-blocking-cohorts"],
    },
  },
};

export const defaultConfig: IpBlockingTrendConfig = {
  title: "Blocking Trend",
  showConfirmed: false,
  showDetails: false,
};

const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };

// ── Formatting ────────────────────────────────────────────────────────────────

const countFormat = new Intl.NumberFormat("en");
const dayLabel = createDateFormatter("utc", { month: "short", day: "numeric" }, "en");
const dayLabelLong = createDateFormatter(
  "utc",
  {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  },
  "en",
);

export function formatCount(value: number | null): string {
  return value === null ? "Unavailable" : countFormat.format(value);
}

/** A tenant-local `YYYY-MM-DD` day is a calendar date: format it as UTC so the viewer's zone never shifts it. */
function parseDay(day: string): Date | null {
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDay(day: string, long = false): string {
  const date = parseDay(day);
  if (!date) return day;
  return (long ? dayLabelLong : dayLabel).format(date);
}

/** Points in day order, whatever order the source sent them in; unparseable days are dropped. */
export function sortedPoints(data: IpBlockingSeriesData) {
  return data.points
    .filter((point) => parseDay(point.day) !== null)
    .sort((a, b) => a.day.localeCompare(b.day));
}

export function describeIpBlockingChart(data: IpBlockingSeriesData): string {
  const points = sortedPoints(data);
  const target = data.targetKind === "DOMAIN" ? "domain" : "IP";
  if (points.length === 0) return `No ${target} blocking data.`;
  const last = points[points.length - 1];
  const seen = data.dateBasis === "blocking_window" ? "distinct" : "detected";
  return `Daily ${seen} ${target} bars and safe-to-block ${target} line for the latest seven days. On ${formatDay(last.day, true)}: ${formatCount(last.detected)} ${seen}, ${formatCount(last.safeToBlock ?? null)} safe to block.`;
}

// ── Derived facts ─────────────────────────────────────────────────────────────

export interface IpBlockingSummary {
  latest: {
    day: string;
    detected: number | null;
    sent: number | null;
    ratio: number | null;
  } | null;
  peak: { day: string; detected: number } | null;
  /** The most recent day whose confirmations are settled. */
  latestConfirmed: { day: string; confirmed: number } | null;
}

export function deriveIpBlockingSummary(data: IpBlockingSeriesData): IpBlockingSummary {
  const points = sortedPoints(data);
  const last = points[points.length - 1];
  let peak: IpBlockingSummary["peak"] = null;
  let latestConfirmed: IpBlockingSummary["latestConfirmed"] = null;
  for (const point of points) {
    if (point.detected !== null && (!peak || point.detected > peak.detected))
      peak = { day: point.day, detected: point.detected };
    if (point.confirmedBlocked !== null)
      latestConfirmed = { day: point.day, confirmed: point.confirmedBlocked };
  }
  return {
    latest: last
      ? {
          day: last.day,
          detected: last.detected,
          sent: last.sent,
          ratio:
            data.coverage?.correlated !== false &&
            last.detected !== null &&
            last.detected > 0 &&
            last.sent !== null
              ? last.sent / last.detected
              : null,
        }
      : null,
    peak,
    latestConfirmed,
  };
}

// ── Chart option ──────────────────────────────────────────────────────────────

export function buildOption(
  data: IpBlockingSeriesData,
  colors: ChartColors,
  options?: {
    showConfirmed?: boolean;
    labels?: IpBlockingLabels;
    missingData?: MissingDataMode;
    cohortSeries?: CohortSeriesOptions;
    /** Draws the chart in (see `useChartAnimation`); absent keeps the option's own default. */
    animate?: boolean;
  },
): IpBlockingOption {
  const option = buildChartOption(data, colors, options);
  return options?.animate === undefined ? option : { ...option, animation: options.animate };
}

function buildChartOption(
  data: IpBlockingSeriesData,
  colors: ChartColors,
  options?: {
    showConfirmed?: boolean;
    labels?: IpBlockingLabels;
    missingData?: MissingDataMode;
    cohortSeries?: CohortSeriesOptions;
  },
): IpBlockingOption {
  const labels =
    options?.labels ??
    (data.targetKind === "DOMAIN"
      ? { ...DEFAULT_LABELS, yAxis: "Unique domains" }
      : DEFAULT_LABELS);
  if (data.cohorts || data.points.some((point) => "safeToBlock" in point))
    return buildCohortOption(
      data,
      colors,
      labels,
      formatDay,
      options?.cohortSeries,
      options?.missingData,
    );
  const showConfirmed = options?.showConfirmed ?? false;
  const points = sortedPoints(data);
  // Detection is the detection lane, sending for blocking is the response lane
  // (blocking is the response), confirmations use the safe classification tone.
  const detectedColor = colors.operational.detection;
  const sentColor = colors.operational.response;
  const confirmedColor = colors.classification?.safe ?? colors.series[1];

  const line = (
    name: string,
    color: string,
    values: (number | null)[],
    extra?: Partial<LineSeriesOption>,
  ): LineSeriesOption => ({
    name,
    type: "line",
    data: values,
    smooth: 0.25,
    symbol: "circle",
    symbolSize: 8,
    connectNulls: false,
    lineStyle: { width: 3, color, cap: "round", join: "round" },
    // A ring in the card color keeps each marker readable where it crosses a bar.
    itemStyle: { color, borderColor: colors.tooltipBg, borderWidth: 2.5 },
    emphasis: { scale: 1.3 },
    z: 3,
    ...extra,
  });

  const legendNames = [labels.detected, labels.sent, ...(showConfirmed ? [labels.confirmed] : [])];

  return {
    animationDuration: 400,
    animationEasing: "cubicOut",
    backgroundColor: "transparent",
    grid: { top: 44, right: 24, bottom: 8, left: 36, containLabel: true },
    legend: {
      top: 4,
      left: "center",
      data: legendNames,
      icon: CHART_LEGEND_ICONS.rect,
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 16,
      type: "scroll",
      textStyle: { color: colors.label, fontSize: colors.typography?.labelSize ?? 12, ...fontBase },
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      position: (point, _params, _dom, _rect, size) => positionChartTooltip(point, size),
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: {
        color: colors.tooltipText,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      axisPointer: { type: "shadow", shadowStyle: { color: withAlpha(colors.label, 0.08) } },
      formatter: (params: unknown) => {
        const items = (Array.isArray(params) ? params : [params]) as Array<{
          seriesName?: string;
          marker?: string;
          axisValue?: string;
          value?: number | null;
        }>;
        const day = items[0]?.axisValue;
        if (typeof day !== "string") return "";
        const rows = items
          .filter((item) => item.seriesName)
          .map((item) => {
            const value = item.value;
            return `<div style="display:flex;justify-content:space-between;gap:16px"><span>${item.marker ?? ""}${escapeHtml(item.seriesName ?? "")}</span><b>${typeof value === "number" ? formatCount(value) : escapeHtml(labels.notSettled)}</b></div>`;
          })
          .join("");
        return `<div style="margin-bottom:4px;opacity:0.75">${escapeHtml(formatDay(day, true))}</div>${rows}`;
      },
    },
    xAxis: {
      type: "category",
      data: points.map((point) => point.day),
      axisLabel: {
        color: colors.label,
        hideOverlap: true,
        margin: 10,
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
        formatter: (value: string) => formatDay(value),
      },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.axisLine } },
    },
    yAxis: {
      type: "value",
      min: 0,
      name: labels.yAxis,
      nameLocation: "middle",
      nameRotate: 90,
      nameGap: 46,
      nameTextStyle: {
        color: withAlpha(colors.label, 0.7),
        fontSize: colors.typography?.labelSize ?? 12,
        ...fontBase,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: colors.grid, type: "solid" } },
      axisLabel: {
        color: colors.label,
        fontSize: colors.typography?.labelSize ?? 12,
        formatter: (value: number) => countFormat.format(value),
        ...fontBase,
      },
    },
    series: [
      {
        name: labels.detected,
        type: "bar",
        data: points.map((point) => point.detected),
        // Wide bars close together, in one flat tint: the day-to-day volume reads as a quiet
        // mass and the line on top as the action taken. No gradient, no glow.
        barMaxWidth: 64,
        barCategoryGap: "22%",
        itemStyle: { borderRadius: [4, 4, 0, 0], color: withAlpha(detectedColor, 0.28) },
        emphasis: { itemStyle: { color: withAlpha(detectedColor, 0.45) } },
      },
      line(
        labels.sent,
        sentColor,
        points.map((point) => point.sent),
      ),
      ...(showConfirmed
        ? [
            line(
              labels.confirmed,
              confirmedColor,
              points.map((point) => point.confirmedBlocked),
              {
                lineStyle: { width: 3, color: confirmedColor, type: "dashed", cap: "round" },
                symbolSize: 8,
              },
            ),
          ]
        : []),
    ],
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

function SummaryStrip({
  summary,
  labels,
}: {
  summary: IpBlockingSummary;
  labels: IpBlockingLabels;
}) {
  const { latest, peak, latestConfirmed } = summary;
  return (
    <dl className="grid shrink-0 grid-cols-1 gap-x-6 gap-y-3 px-(--density-widget-body-padding) pt-(--density-widget-body-padding) @sm/widget:grid-cols-2 @3xl/widget:grid-cols-4">
      <MetricStat
        label={labels.summaryLatest}
        value={latest ? formatCount(latest.sent) : "—"}
        detail={
          latest ? `of ${formatCount(latest.detected)} · ${formatDay(latest.day)}` : undefined
        }
      />
      <MetricStat
        label={labels.summaryRatio}
        value={latest?.ratio != null ? `${Math.round(latest.ratio * 100)}%` : "—"}
        detail={latest ? formatDay(latest.day) : undefined}
      />
      <MetricStat
        label={labels.summaryPeak}
        value={peak ? formatCount(peak.detected) : "—"}
        detail={peak ? formatDay(peak.day) : undefined}
      />
      <MetricStat
        label={labels.summaryConfirmed}
        value={latestConfirmed ? formatCount(latestConfirmed.confirmed) : "—"}
        detail={latestConfirmed ? formatDay(latestConfirmed.day) : undefined}
      />
    </dl>
  );
}

/** The latest day at a glance: the first shown series, its share and the detected total. */
function CohortHeadline({
  data,
  animateValue,
  series,
}: {
  data: IpBlockingSeriesData;
  animateValue: boolean;
  series: Required<CohortSeriesOptions>;
}) {
  const points = sortedPoints(data);
  // The latest day with data: a trailing day not measured yet says nothing.
  const last = ([...points].reverse().find((point) => point.detected != null) ??
    points[points.length - 1]) as (typeof points)[number] | undefined;
  const target = data.targetKind === "DOMAIN" ? "domains" : "IPs";
  const seenTargets =
    data.dateBasis === "blocking_window" ? `distinct ${target}` : `${target} detected`;
  // Without the safe line, the headline follows the first shown series.
  const isDetected = !series.showSafe && (series.showDetected || !series.showApproved);
  const headline = series.showSafe
    ? { value: last?.safeToBlock ?? null, text: "safe to block", tone: "text-status-ok-text" }
    : isDetected
      ? { value: last?.detected ?? null, text: seenTargets, tone: "text-primary" }
      : { value: last?.approved ?? null, text: "regulator approved", tone: "text-brand-purple" };
  const motion = useWidgetMotion();
  const shown = useAnimatedNumber(headline.value, {
    duration: 1100,
    enabled: motion && animateValue,
  });
  if (!last) return null;
  const value = headline.value;
  const share =
    value != null && last.detected ? `${Math.round((value / last.detected) * 100)}%` : null;
  return (
    <div
      className={`${motionClass(motion, "nx-rise")} flex min-w-0 shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1 px-(--density-widget-body-padding) pt-(--density-widget-body-padding)`}
    >
      <span
        className={`text-3xl font-bold tabular-nums tracking-display ${value == null ? "text-muted" : headline.tone}`}
      >
        {value == null ? "—" : formatCount(Math.round(shown ?? value))}
      </span>
      <span className="min-w-0 text-metric-label text-secondary">
        {headline.text} on {formatDay(last.day, true)}
      </span>
      {isDetected ? null : (
        <span className="min-w-0 text-metric-detail text-muted">
          {last.detected == null
            ? `${data.dateBasis === "blocking_window" ? "Distinct" : "Detected"} ${target} unavailable`
            : `${share ? `${share} of ` : ""}${formatCount(last.detected)} ${seenTargets}`}
        </span>
      )}
    </div>
  );
}

function IpBlockingTrendComponent({ data, config }: WidgetProps<IpBlockingTrendConfig>) {
  const timeOnly = createDateFormatter(useDisplayTimezone(), {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen: inFocusOverlay } = useContext(WidgetFullscreenContext);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const hasRenderedRef = useRef(false);

  const error = firstError(data, ["series"]);
  const series = unwrap<IpBlockingSeriesData>(data.series);
  const isDomain = series?.targetKind === "DOMAIN";
  const isLoading = data.series?.isLoading ?? false;
  const configuredTitle = config.title?.trim();
  const title =
    !configuredTitle ||
    [
      "IPs detected vs sent for blocking",
      "IP and domain blocking",
      "IP blocking",
      "Domain blocking",
      defaultConfig.title,
    ].includes(configuredTitle)
      ? defaultConfig.title
      : configuredTitle;
  const showConfirmed = config.showConfirmed ?? false;
  const showDetails = config.showDetails ?? false;
  const missingData = missingDataMode(config.missingData);
  const showDetected = config.showDetected ?? true;
  const showSafe = config.showSafe ?? true;
  const showApproved = config.showApproved ?? false;
  const showDetectedLabels = config.showDetectedLabels ?? false;
  const showSafeLabels = config.showSafeLabels ?? true;
  const showApprovedLabels = config.showApprovedLabels ?? false;
  const cohortSeries = useMemo(
    () => ({
      showDetected,
      showSafe,
      showApproved,
      showDetectedLabels,
      showSafeLabels,
      showApprovedLabels,
    }),
    [showDetected, showSafe, showApproved, showDetectedLabels, showSafeLabels, showApprovedLabels],
  );
  const labelsJson = JSON.stringify(
    resolveLabels(
      LABELS,
      isDomain
        ? {
            ...config,
            labels: {
              detected: "Domains detected",
              sent: "Domains sent for blocking",
              yAxis: "Unique domains",
              emptyTitle: "No domain blocking data",
              emptyDescription: "No domains were reported for this period.",
              ...config.labels,
            },
          }
        : config,
    ),
  );
  const labels = useMemo(() => JSON.parse(labelsJson) as IpBlockingLabels, [labelsJson]);

  const summary = useMemo(() => (series ? deriveIpBlockingSummary(series) : null), [series]);
  const isEmpty = !isLoading && !error && (!series || series.points.length === 0);
  const showChart = !error && !isEmpty && series !== null;
  const isCohort = Boolean(
    series?.cohorts || series?.points.some((point) => "safeToBlock" in point),
  );
  const firstLoad = isLoading && !hasRenderedRef.current;

  const seriesRef = useRef(series);
  const labelsRef = useRef(labels);
  const showConfirmedRef = useRef(showConfirmed);
  const missingDataRef = useRef(missingData);
  const cohortSeriesRef = useRef(cohortSeries);
  const animate = useChartAnimation(config);
  const animateRef = useRef(animate);
  useEffect(() => {
    animateRef.current = animate;
    seriesRef.current = series;
    labelsRef.current = labels;
    showConfirmedRef.current = showConfirmed;
    missingDataRef.current = missingData;
    cohortSeriesRef.current = cohortSeries;
  }, [series, labels, showConfirmed, missingData, cohortSeries, animate]);

  useEffect(() => {
    const host = chartRef.current;
    if (!host || error || isEmpty || isLoading || !series) return;
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, { renderer: "canvas" });
    }
    // `notMerge`: turning the confirmed series off must remove it, not leave it drawn.
    chartInstanceRef.current.setOption(
      buildOption(series, resolveChartColors(host), {
        labels,
        showConfirmed,
        missingData,
        cohortSeries,
        animate,
      }),
      true,
    );
    hasRenderedRef.current = true;
    markContentReady();
  }, [
    series,
    labels,
    showConfirmed,
    missingData,
    cohortSeries,
    animate,
    error,
    isEmpty,
    isLoading,
    markContentReady,
  ]);

  useChartLifecycle(chartRef, chartInstanceRef, (host, chart) => {
    const current = seriesRef.current;
    if (hasRenderedRef.current && current) {
      chart.setOption(
        buildOption(current, resolveChartColors(host), {
          labels: labelsRef.current,
          showConfirmed: showConfirmedRef.current,
          missingData: missingDataRef.current,
          cohortSeries: cohortSeriesRef.current,
          // A redraw on resize never plays the entry animation again.
          animate: false,
        }),
        true,
      );
    }
  });

  const freshness = series?.freshness;
  const note = [
    isDomain ? "Daily counts of unique domains" : "Daily counts of unique IPs",
    freshness?.dataThrough
      ? `data through ${timeOnly.format(new Date(freshness.dataThrough))}`
      : null,
    freshness?.isEstimated ? "Estimated" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Widget
      title={title}
      intrinsicHeight={!inFocusOverlay}
      actions={isLoading && !firstLoad ? <UpdatingPill /> : undefined}
    >
      <div
        className={`flex w-full flex-col ${inFocusOverlay ? "h-full min-h-0 overflow-y-auto" : "flex-1"}`}
      >
        {series && showChart && isCohort && config.showLatest !== false && (
          <CohortHeadline
            data={series}
            animateValue={config.animateValue === true}
            series={cohortSeries}
          />
        )}
        {summary && showChart && showDetails && !isCohort && (
          <SummaryStrip summary={summary} labels={labels} />
        )}
        <div className="relative w-full flex-1 min-h-(--widget-chart-min-height)">
          <div
            ref={chartRef}
            role="img"
            aria-label={series ? describeIpBlockingChart(series) : "IP blocking chart"}
            className="absolute inset-0 h-full w-full transition-opacity duration-200"
            style={{ opacity: showChart ? 1 : 0 }}
          />
          {error && <WidgetIssueState error={error} className="absolute inset-0" />}
          {isEmpty && (
            <WidgetState
              title={labels.emptyTitle}
              description={labels.emptyDescription}
              className="absolute inset-0"
            />
          )}
          {firstLoad && <WidgetSkeleton shape="chart" className="absolute inset-0" />}
        </div>
        {showChart && showDetails && (
          <p className="shrink-0 px-4 pb-3 pt-2 text-xs text-muted">{note}</p>
        )}
      </div>
    </Widget>
  );
}

export const Component = IpBlockingTrendComponent;

const widgetModule: WidgetModule<IpBlockingTrendConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
